/**
 * 订单业务类型展示：按明细/备注识别特惠、实惠、商品，混合单用「+」拼接。
 * orderType：1 商品 / 2 充值 / 3 实惠 / 4 特惠
 */

export type OrderBizFlags = {
  promo: boolean
  voucher: boolean
  goods: boolean
  recharge: boolean
}

function flavorOf(line: any): string {
  return String((line && (line.dishFlavor || line.flavor)) || '').trim()
}

function nameOf(line: any): string {
  return String((line && line.name) || '')
}

function isPromoLine(line: any): boolean {
  const f = flavorOf(line)
  const n = nameOf(line)
  return (
    f.indexOf('PROMO:') === 0 ||
    f.indexOf('PROMO-GIFT:') === 0 ||
    n.indexOf('特惠套餐') >= 0
  )
}

function isVoucherLine(line: any): boolean {
  const f = flavorOf(line)
  const n = nameOf(line)
  if (isPromoLine(line)) return false
  return (
    f.indexOf('WV:') === 0 ||
    f.indexOf('WV-GIFT:') === 0 ||
    /购\d+送\d+/.test(n) ||
    (n.indexOf('电子票') >= 0 && n.indexOf('特惠') < 0)
  )
}

function isQuotaDeliveryLine(line: any): boolean {
  return flavorOf(line).indexOf('QUOTA:') === 0
}

/** 从订单行 / 备注 / orderType 推断业务构成 */
export function detectOrderBizFlags(row: any): OrderBizFlags {
  const flags: OrderBizFlags = {
    promo: false,
    voucher: false,
    goods: false,
    recharge: false,
  }
  if (!row) return flags

  const t = row.orderType != null ? Number(row.orderType) : NaN
  if (t === 2) {
    flags.recharge = true
    return flags
  }

  const details = row.orderDetailList || row.orderDetails || row.orderDetail || []
  if (Array.isArray(details) && details.length > 0) {
    details.forEach((line: any) => {
      if (!line) return
      if (isPromoLine(line)) flags.promo = true
      else if (isVoucherLine(line)) flags.voucher = true
      else if (isQuotaDeliveryLine(line)) flags.goods = true
      else flags.goods = true
    })
    return flags
  }

  const remark = String(row.remark || '')
  if (remark.indexOf('特惠') >= 0) flags.promo = true
  if (remark.indexOf('实惠') >= 0 || /购\d+送\d+/.test(remark)) flags.voucher = true
  if (remark.indexOf('含单品配送') >= 0) flags.goods = true

  if (!flags.promo && !flags.voucher && !flags.goods) {
    if (t === 4) flags.promo = true
    else if (t === 3) flags.voucher = true
    else if (t === 1 || Number.isNaN(t)) flags.goods = true
  } else {
    // 备注已识别到部分类型时，仍用 orderType 补主类型
    if (t === 4) flags.promo = true
    if (t === 3) flags.voucher = true
  }
  return flags
}

/** 后台赠送配送单（0 元、不扣用户额度；仅认固定备注前缀 / ADMIN-GIFT 明细，不含入账单） */
export function isAdminGiftDeliveryOrder(row?: any | null): boolean {
  if (!row) return false
  const remark = String(row.remark || '').trim()
  if (remark.indexOf('赠送配送') === 0) return true
  if (remark.indexOf('赠品立即配送') === 0) return true
  const details = row.orderDetailList || row.orderDetails || row.orderDetail || []
  if (Array.isArray(details)) {
    for (let i = 0; i < details.length; i++) {
      const f = String((details[i] && (details[i].dishFlavor || details[i].flavor)) || '')
      if (f.indexOf('ADMIN-GIFT:') === 0) return true
    }
  }
  return false
}

/** 水券/额度抵扣的配送履约单（非购票付款单） */
export function isWaterVoucherFulfillmentOrder(row?: any | null): boolean {
  if (!row) return false
  const t = row.orderType != null ? Number(row.orderType) : NaN
  const pm = row.payMethod != null ? Number(row.payMethod) : NaN
  if (pm !== 4) return false
  return Number.isNaN(t) || t === 1
}

/** 订单管理不应展示的非付款履约单：水券配送 / 赠品配送 / 商家赠送入账 */
export function isNonPaymentFulfillmentOrder(row?: any | null): boolean {
  return (
    isAdminGiftDeliveryOrder(row) ||
    isAdminGiftCreditOrder(row) ||
    isWaterVoucherFulfillmentOrder(row)
  )
}

/** 后台商家赠送入账单（套餐形态、用户订单可见、非派送） */
export function isAdminGiftCreditOrder(row?: any | null): boolean {
  if (!row) return false
  const remark = String(row.remark || '').trim()
  if (remark.indexOf('商家赠送') === 0) return true
  const details = row.orderDetailList || row.orderDetails || row.orderDetail || []
  if (Array.isArray(details)) {
    for (let i = 0; i < details.length; i++) {
      const f = String((details[i] && (details[i].dishFlavor || details[i].flavor)) || '')
      if (f.indexOf('ADMIN-CREDIT:') === 0) return true
    }
  }
  return false
}

/** 列表/详情类型文案 */
export function formatOrderBizType(row?: any | null): string {
  if (!row) return '—'
  if (isAdminGiftDeliveryOrder(row) || isAdminGiftCreditOrder(row)) return '赠送'
  const flags = detectOrderBizFlags(row)
  if (flags.recharge) return '充值'

  const parts: string[] = []
  if (flags.promo) parts.push('特惠')
  if (flags.voucher) parts.push('实惠')
  if (flags.goods) parts.push('商品')
  if (parts.length) return parts.join('+')

  const t = row.orderType != null ? Number(row.orderType) : NaN
  if (t === 4) return '特惠'
  if (t === 3) return '实惠'
  if (t === 2) return '充值'
  if (t === 1 || Number.isNaN(t)) return '商品'
  return '—'
}
